<template>
    <div class="mb-8">

        <div class="overflow-hidden rounded-md border border-gray-200 bg-white">

            <div class="border-b border-gray-200 bg-gray-50 px-6 py-4">
                <div class="flex items-center justify-between">

                    <div>
                        <h2 class="m-0 p-0 text-left text-lg font-semibold text-gray-900">{{ title }}</h2>
                        <p class="mt-1 mb-0 text-sm text-gray-600">{{ subtitle }}</p>
                    </div>

                    <div class="flex items-center gap-4">
                        <div class="flex items-center gap-2">

                            <div class="relative">
                                <input
                                    v-model="searchQuery"
                                    type="text"
                                    placeholder="Search installations"
                                    class="rounded-md border-gray-300 py-2 pl-10 pr-10 text-sm focus:border-blue-500 focus:ring-blue-500"
                                    @keydown.enter="applySearch"
                                />

                                <svg class="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><circle cx="11" cy="11" r="8"></circle><path d="m21 21-4.35-4.35"></path></svg>

                                <button
                                    v-if="searchQuery"
                                    @click="clearSearch"
                                    type="button"
                                    class="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 focus:outline-none"
                                >
                                    <svg class="h-4 w-4" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="10"></circle><line x1="15" y1="9" x2="9" y2="15"></line><line x1="9" y1="9" x2="15" y2="15"></line></svg>
                                </button>
                            </div>

                            <button
                                @click="applySearch"
                                class="inline-flex items-center rounded-md border border-transparent bg-blue-600 px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
                            >
                                <svg class="mr-2 h-4 w-4" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"><circle cx="11" cy="11" r="8"></circle><path d="m21 21-4.35-4.35"></path></svg>
                                Search
                            </button>

                            <select
                                v-model="itemsPerPage"
                                class="block rounded-md border-gray-300 bg-white text-sm text-gray-700 transition-colors py-2 focus:border-blue-500 focus:ring-blue-500"
                                @change="handlePerPageChange"
                            >
                                <option :value="10">10 per page</option>
                                <option :value="25">25 per page</option>
                                <option :value="50">50 per page</option>
                                <option :value="100">100 per page</option>
                            </select>

                        </div>
                    </div>

                </div>
            </div>

            <div class="overflow-x-auto">
                <table class="w-full divide-y divide-gray-200">
                    <thead class="bg-gray-50">

                    <tr>
                        <th class="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500" :class="columnWidth">
                            Installation
                        </th>

                        <th v-if="showCompanyColumn" class="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500" :class="columnWidth">
                            Company
                        </th>

                        <slot name="additional-headers"></slot>

                        <th class="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500" :class="columnWidth">
                            Actions
                        </th>
                    </tr>

                    </thead>

                    <tbody class="bg-white divide-y divide-gray-200">

                    <tr v-for="installation in installationData" :key="installation.id">
                        <td class="px-6 py-4">

                            <div class="flex flex-col gap-1.5">
                                <div class="flex items-center gap-2">
                                    <p class="mb-0 text-sm font-semibold text-gray-900">{{ installation.name }}</p>

                                    <slot name="name-after" :installation="installation"></slot>
                                </div>

                                <p class="mb-0 text-xs text-gray-600">{{ installation.address }}</p>
                            </div>

                        </td>

                        <td v-if="showCompanyColumn" class="px-6 py-4">

                            <p class="mb-0 text-sm font-medium text-gray-900">{{ installation.company?.name }}</p>

                        </td>

                        <slot name="additional-columns" :installation="installation"></slot>

                        <td class="px-6 py-4">

                            <slot name="actions" :installation="installation"></slot>

                        </td>
                    </tr>

                    </tbody>
                </table>
            </div>

            <div v-if="hasPagination" class="border-t border-gray-200 bg-gray-50 px-6 py-4">
                <div class="flex items-center justify-between">

                    <div class="text-sm text-gray-700">
                        Showing <span class="font-medium">{{ paginationMeta.from }}</span> to <span class="font-medium">{{ paginationMeta.to }}</span> of <span class="font-medium">{{ paginationMeta.total }}</span> results
                    </div>

                    <div class="flex gap-2">
                        <a
                            v-for="link in paginationLinks"
                            :key="link.label"
                            :href="link.url || '#'"
                            :class="[
                                'inline-flex items-center rounded-md border px-4 py-2 text-sm font-medium transition-colors',
                                link.active ? 'border-blue-500 bg-blue-50 text-blue-600' : link.url ? 'border-gray-300 bg-white text-gray-700 hover:bg-gray-50' : 'border-gray-300 bg-gray-100 text-gray-400 cursor-not-allowed'
                            ]"
                            :aria-disabled="!link.url"
                            @click.prevent="link.url && navigateToPage(link.url)"
                            v-html="link.label"
                        ></a>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script setup>
import {computed, ref} from 'vue'

/**
 * @typedef {import('./types/types').Installation} Installation
 */

const props = defineProps({
    title: {
        type: String,
        required: true
    },
    installations: {
        /** @type {import('vue').PropType<Installation[]>} */
        type: [Array, Object],
        required: true
    },
    showCompanyColumn: {
        type: Boolean,
        default: false
    }
})

const searchQuery = ref('')
const itemsPerPage = ref(25)

const installationData = computed(() => {
    return props.installations.data || []
})

const hasPagination = computed(() => {
    return props.installations.meta && props.installations.meta.last_page > 1
})

const paginationLinks = computed(() => {
    return props.installations.meta.links || []
})

const paginationMeta = computed(() => {
    return props.installations.meta || {
        from: 0,
        to: 0,
        total: 0,
        current_page: 1,
        last_page: 1
    }
})

const getCurrentUrlParams = () => {
    const url = new URL(window.location.href)
    return new URLSearchParams(url.search)
}

const buildUrl = (params) => {
    const url = new URL(window.location.href)
    const searchParams = new URLSearchParams(url.search)

    Object.keys(params).forEach(key => {
        if (params[key] !== null && params[key] !== undefined && params[key] !== '') {
            searchParams.set(key, params[key])
        } else {
            searchParams.delete(key)
        }
    })

    url.search = searchParams.toString()
    return url.toString()
}

const navigateToPage = (url) => {
    if (searchQuery.value) {
        const urlObj = new URL(url)
        urlObj.searchParams.set('search', searchQuery.value)
        url = urlObj.toString()
    }

    if (itemsPerPage.value !== 25) {
        const urlObj = new URL(url)
        urlObj.searchParams.set('per_page', itemsPerPage.value)
        url = urlObj.toString()
    }

    window.location.href = url
}

const applySearch = () => {
    window.location.href = buildUrl({
        search: searchQuery.value || null,
        per_page: itemsPerPage.value !== 25 ? itemsPerPage.value : null,
        page: 1
    })
}

const clearSearch = () => {
    searchQuery.value = ''

    window.location.href = buildUrl({
        search: null,
        per_page: itemsPerPage.value !== 25 ? itemsPerPage.value : null,
        page: 1
    })
}

const handlePerPageChange = () => {
    window.location.href = buildUrl({
        search: searchQuery.value || null,
        per_page: itemsPerPage.value !== 25 ? itemsPerPage.value : null,
        page: 1
    })
}

const initializeFromUrl = () => {
    const params = getCurrentUrlParams()
    const searchParam = params.get('search')
    const perPageParam = params.get('per_page')

    if (searchParam) {
        searchQuery.value = searchParam
    }

    if (perPageParam) {
        itemsPerPage.value = parseInt(perPageParam)
    }
}

initializeFromUrl()

const subtitle = computed(() => {
    const count = paginationMeta.value.total || 0
    const plural = count === 1 ? 'installation' : 'installations'
    return `${count} ${plural}`
})

const columnWidth = computed(() => {
    if (props.showCompanyColumn) {
        return 'w-1/5'
    }
    return 'w-1/4'
})
</script>